import React, { useMemo, useState } from 'react';
import { Box, Chip, makeStyles, createStyles, Tooltip, Typography } from '@material-ui/core';
import { parseISO } from 'date-fns';
import { GridSelectionModel } from '@mui/x-data-grid-pro';
import { Reservation } from '@lib/state';
import {
  formatCurrency,
  getAverageRateInRange,
  getInRangeRateAmounts,
  getRatePlanForRange,
} from '../bulk-change-billing-utils';
import {
  DataTable,
  IDataTableColumn,
  DateDisplay,
  Link,
  ShowMoreButton,
  useLookup,
  VipDisplay,
  CurrencyDisplay,
} from '@lib/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  cellNoWrapStyle,
  cellTruncateStyle,
  tabularNumsStyle,
  compactTableStyle,
} from '../bulk-change-billing-styles';

interface Props {
  reservations: Reservation[];
  selectedReservations: Reservation[];
  onSelectionChange: (selected: Reservation[]) => void;
  isDone: boolean;
  loadMore: () => void;
  /** Effective start date used to restrict the Current Rate column average. */
  effectiveStartDate: Date;
  /** Effective end date used to restrict the Current Rate column average. */
  effectiveEndDate: Date;
}

const useStyles = makeStyles(theme =>
  createStyles({
    icon: {
      color: theme.palette.secondary.main,
      fontSize: '0.75rem',
    },
    compactTable: compactTableStyle(theme),
    cellNoWrap: cellNoWrapStyle,
    cellTruncate: cellTruncateStyle,
    tabularNums: tabularNumsStyle,
    // Merged rate column: amount + plan name stacked
    ratePrimary: {
      fontWeight: 500,
      whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums',
    },
    rateSecondary: {
      fontSize: '0.7rem',
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 120,
      lineHeight: 1.2,
    },
    // Compact reservation ID cell
    resIdCell: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      whiteSpace: 'nowrap',
    },
    filterBar: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      padding: theme.spacing(1, 0),
    },
    filterLabel: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(0.5),
      fontWeight: 500,
    },
  })
);

export const BulkChangeBillingReservationList: React.FC<Props> = ({
  reservations,
  selectedReservations,
  onSelectionChange,
  isDone,
  loadMore,
  effectiveStartDate,
  effectiveEndDate,
}) => {
  const styles = useStyles();
  const reservationLookup = useLookup(reservations, x => x.id);
  const [rateFilter, setRateFilter] = useState<number | undefined>(undefined);

  // Distinct in-range standard rate amounts across all reservations (sorted asc).
  const availableRateAmounts = useMemo(() => {
    const set = new Set<number>();
    for (const res of reservations) {
      for (const amt of getInRangeRateAmounts(res, effectiveStartDate, effectiveEndDate)) {
        set.add(amt);
      }
    }
    return [...set].sort((a, b) => a - b);
  }, [reservations, effectiveStartDate, effectiveEndDate]);

  // If the active filter no longer matches any reservation, clear it.
  if (rateFilter != null && !availableRateAmounts.includes(rateFilter)) {
    setRateFilter(undefined);
  }

  const filteredReservations = useMemo(() => {
    const filtered =
      rateFilter == null
        ? reservations
        : reservations.filter(res =>
            getInRangeRateAmounts(res, effectiveStartDate, effectiveEndDate).includes(rateFilter)
          );
    return [...filtered].sort((a, b) => b.recordNumber - a.recordNumber);
  }, [reservations, rateFilter, effectiveStartDate, effectiveEndDate]);

  // Only pass selection ids that are currently visible to the grid.
  // Passing hidden ids causes DataGridPro to fire onSelectionModelChange to
  // remove them, which combined with our preserve-hidden merge would cause
  // an infinite render loop.
  const visibleSelectionModel = useMemo(() => {
    const visibleIds = new Set(filteredReservations.map(r => r.id));
    return selectedReservations.filter(r => visibleIds.has(r.id)).map(r => r.id);
  }, [filteredReservations, selectedReservations]);

  const columns: IDataTableColumn<Reservation>[] = useMemo(
    () => [
      {
        title: 'Res #',
        valueFactory: res => <ReservationIdCell res={res} />,
        colSpan: 0.6,
      },
      {
        title: 'Guest',
        valueFactory: res => {
          const name = res.contacts.length > 0 ? res.contacts[0].name : undefined;
          const fullName = name ? `${name.last ?? ''}, ${name.first ?? ''}`.trim() : '—';
          const hasVip = res.contacts.length > 0 && res.contacts[0].vipStatus;
          return (
            <Tooltip title={fullName} placement="top" arrow>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Typography variant="body2" className={styles.cellTruncate}>
                  {fullName}
                </Typography>
                {hasVip && (
                  <VipDisplay vipStatus={res.contacts[0].vipStatus} key={res.contacts[0].userId} />
                )}
              </div>
            </Tooltip>
          );
        },
        colSpan: 1.2,
      },
      {
        title: 'Check In',
        valueFactory: res => (
          <DateDisplay
            date={parseISO(res.checkInDate)}
            dateFormat="M/d/yyyy"
            variant="body2"
            className={styles.cellNoWrap}
          />
        ),
        colSpan: 0.65,
      },
      {
        title: 'Check Out',
        valueFactory: res => (
          <DateDisplay
            date={parseISO(res.checkOutDate)}
            dateFormat="M/d/yyyy"
            variant="body2"
            className={styles.cellNoWrap}
          />
        ),
        colSpan: 0.65,
      },
      {
        title: 'Room',
        valueFactory: res => (
          <Tooltip title={res.rooms[0]?.roomType ?? ''} placement="top" arrow>
            <Typography variant="body2" className={styles.cellTruncate}>
              {res.rooms[0]?.roomType ?? '—'}
            </Typography>
          </Tooltip>
        ),
        colSpan: 0.8,
      },
      {
        title: 'Rate Over Effective Dates',
        align: 'right',
        valueFactory: res => {
          const avg = getAverageRateInRange(res, effectiveStartDate, effectiveEndDate);
          const planName = getRatePlanForRange(res, effectiveStartDate, effectiveEndDate);
          return (
            <Tooltip title={planName ? `Rate Plan: ${planName}` : ''} placement="top" arrow>
              <div style={{ textAlign: 'right' }}>
                <CurrencyDisplay value={avg} variant="body2" className={styles.ratePrimary} />
                {planName && (
                  <Typography className={styles.rateSecondary} component="div">
                    {planName}
                  </Typography>
                )}
              </div>
            </Tooltip>
          );
        },
        colSpan: 0.9,
      },
    ],
    [styles, effectiveStartDate, effectiveEndDate]
  );

  return (
    <>
      {availableRateAmounts.length > 0 && (
        <Box className={styles.filterBar}>
          <Typography variant="caption" className={styles.filterLabel}>
            Filter by rate:
          </Typography>
          <Chip
            size="small"
            label="All"
            color={rateFilter == null ? 'primary' : 'default'}
            variant={rateFilter == null ? 'default' : 'outlined'}
            onClick={() => setRateFilter(undefined)}
          />
          {availableRateAmounts.map(amount => (
            <Chip
              key={amount}
              size="small"
              label={formatCurrency(amount)}
              color={rateFilter === amount ? 'primary' : 'default'}
              variant={rateFilter === amount ? 'default' : 'outlined'}
              onClick={() => setRateFilter(prev => (prev === amount ? undefined : amount))}
            />
          ))}
        </Box>
      )}
      <DataTable
        items={filteredReservations}
        getItemKey={res => res.id}
        checkboxSelection={true}
        onSelectionModelChange={(model: GridSelectionModel) => {
          // The grid's selection model only reflects rows currently visible
          // (i.e., after the chip filter is applied). To preserve selections
          // for reservations that are hidden by the active filter, merge the
          // visible model with previously-selected rows that aren't currently
          // shown.
          const visibleIds = new Set(filteredReservations.map(r => r.id));
          const preserved = selectedReservations.filter(r => !visibleIds.has(r.id));
          const fromModel = model.map(x => reservationLookup.get(x as string)!).filter(Boolean);
          onSelectionChange([...preserved, ...fromModel]);
        }}
        selectionModel={visibleSelectionModel}
        columns={columns}
        stickyHeader
        tableClass={styles.compactTable}
      >
        <ShowMoreButton isDone={isDone} loadMore={loadMore} />
      </DataTable>
    </>
  );
};

// ---------------------------------------------------------------------------
// Compact reservation ID cell — icon + link on one line
// ---------------------------------------------------------------------------
function ReservationIdCell({ res }: { res: Reservation }) {
  const styles = useStyles();
  const hasMissingCostCenter = res.fields.some(c => c.isRequired && c.value === '');

  return (
    <Tooltip title={hasMissingCostCenter ? 'Missing cost center field info' : ''}>
      <div className={styles.resIdCell}>
        <Link to={`/reservations/${res.id}`} target="_blank" rel="noopener noreferrer">
          {res.recordNumber}
        </Link>{' '}
        {hasMissingCostCenter && (
          <FontAwesomeIcon icon="exclamation-circle" className={styles.icon} />
        )}
      </div>
    </Tooltip>
  );
}
